import { shallowRef, ref, Ref, computed } from '@vue/composition-api';
import { isNil } from 'lodash';

import { transformLayoutData, transFormToUsedField } from './transformData';
import { IGNORE_FIELDS_IN_LAYOUT } from '../common/config';
import { useGetter } from 'common-module/src/util/useVuex';
import { isOverseasVersion } from 'common-module/src/util';
import {
  LayoutAreaInFields,
  LayoutFieldItem,
  LayoutAreaId,
  unusedAreaFieldModel,
  AreaCodeModel,
  EditFieldParam
} from '../model/layout';
import {
  loadLayout,
  loadUnusedFields
} from '../api/layout';

export const areaIdMap: { top: 0; left: 1; right: 2 } = {
  'top': 0,
  'left': 1,
  'right': 2
};

export const AREA_CODE_MAP = {
  left: 'leftAreaFields',
  right: 'rightAreaFields',
  unused: 'unusedAreaFields'
};


export const useLayout = () => {
  // 布局内字段列表
  const oldLayoutFields = shallowRef<LayoutFieldItem[]>([]);
  const ignoreFields = shallowRef<LayoutFieldItem[]>([]);
  const leftAreaFields = ref<LayoutFieldItem[]>([]);
  const rightAreaFields = ref<LayoutFieldItem[]>([]);
  const unusedAreaFields = ref<LayoutFieldItem[]>([]);
  // 已加入布局的字段集合
  const usedFields = computed(() => [...leftAreaFields.value, ...rightAreaFields.value, ...ignoreFields.value]);

  // 加载布局数据
  const layoutLoading = ref<boolean>(false);
  const loadLayoutData = async (
    projectId: number,
    issueTypeId: number
  ): Promise<void> => {
    if (isNil(projectId) || isNil(issueTypeId)) {
      return;
    }
    try {
      layoutLoading.value = true;
      const data = await loadLayout({ projectId, issueTypeId });
      const layoutAreas: LayoutAreaInFields[] = transformLayoutData(data);

      leftAreaFields.value = filterIgnoreField(getAreaFields(layoutAreas, areaIdMap.left));
      rightAreaFields.value = filterIgnoreField(getAreaFields(layoutAreas, areaIdMap.right));

      ignoreFields.value = (layoutAreas || [])
        .map(area => (area.fields || []))
        .flat()
        .filter(l => IGNORE_FIELDS_IN_LAYOUT.includes(l.fieldCode));
      
      // 记录旧布局数据
      oldLayoutFields.value = [...leftAreaFields.value, ...rightAreaFields.value, ...ignoreFields.value];
    } catch (err) {
      console.error(err);
    } finally {
      layoutLoading.value = false;
    }
  };

  // 获取区域字段列表
  const getAreaFields = (
    areas: LayoutAreaInFields[],
    areaId: LayoutAreaId
  ): LayoutFieldItem[] => {
    if (!Array.isArray(areas) || isNil(areaId)) {
      return [];
    }
    return areas.find(item => item.areaId === areaId)?.fields || [];
  };

  // 过滤忽略字段
  const filterIgnoreField = (areaFields: LayoutFieldItem[]): LayoutFieldItem[] =>
    (areaFields || []).filter(l => !IGNORE_FIELDS_IN_LAYOUT.includes(l.fieldCode) && switchIgnoreField(l.fieldCode));

  // 功能开关控制字段
  const switchIgnoreField = (fieldCode: string) => {
    const switchKeyMap: Record<string, any> = {
      'docs': isOverseasVersion ? undefined : 'doc_switch'
    };
    return switchKeyMap[fieldCode] ? useGetter('features/getFeature')?.(switchKeyMap[fieldCode]) : true;
  };

  // 加载未使用字段列表
  const fieldsLoading = ref<boolean>(false);
  const loadUnusedField = async (
    projectId: number,
    issueTypeId: number
  ) => {
    try {
      fieldsLoading.value = true;
      const unusedFields: unusedAreaFieldModel[] = await loadUnusedFields({ projectId, issueTypeId });
      unusedAreaFields.value = transFormToUsedField(unusedFields);
    } catch (err) {
      console.error(err);
    } finally {
      fieldsLoading.value = false;
    }
  };

  // 更新布局字段
  const updateLayoutField = (fields: LayoutFieldItem[], areaCode: AreaCodeModel) => {
    switch (areaCode) {
      case 'left':
        leftAreaFields.value = fields || [];
        break;
      case 'right':
        rightAreaFields.value = fields || [];
        break;
      case 'unused':
        unusedAreaFields.value = fields || [];
        break;
      default:
        return;
    }
  };

  // 字段加入当前布局
  const fieldInsertLayout = (
    field: LayoutFieldItem,
    toAreas: Ref<LayoutFieldItem[]>,
    index?: number
  ): void => {
    if (!Array.isArray(toAreas.value) || isNil(field)) {
      return;
    }
    if (!isNil(index)) {
      toAreas.value.splice(index, 0, field); // 按位置插入布局
      return;
    }
    toAreas.value.push(field);
  };

  // 更新某区域字段信息
  const updateFieldInLayout = (
    field: LayoutFieldItem | EditFieldParam,
    toAreas: Ref<LayoutFieldItem[]>
  ): void => {
    if (!Array.isArray(toAreas.value) || isNil(field)) {
      return;
    }
    const findIndex = toAreas.value.findIndex(f => f.customFieldId === field.customFieldId);
    if (findIndex === -1) {
      return;
    }
    const oldField = toAreas.value.splice(findIndex, 1);
    toAreas.value.splice(findIndex, 0, { ...oldField[0], ...field });
  };

  // 区域内删除字段
  const deletedFieldsInArea = (
    field: LayoutFieldItem,
    fromAreas: Ref<LayoutFieldItem[]>
  ) => {
    if (!Array.isArray(fromAreas.value) || isNil(field)) {
      return;
    }
    const findIndex = fromAreas.value.findIndex(f => f.customFieldId === field.customFieldId);
    if (findIndex === -1) {
      return;
    }
    return fromAreas.value.splice(findIndex, 1);
  };

  const onClearLayout = () => {
    oldLayoutFields.value = [];
    ignoreFields.value = [];
    leftAreaFields.value = [];
    rightAreaFields.value = [];
    unusedAreaFields.value = [];
  };


  return {
    oldLayoutFields,
    ignoreFields,
    leftAreaFields,
    rightAreaFields,
    unusedAreaFields,
    usedFields,
    layoutLoading,
    fieldsLoading,
    loadLayoutData,
    loadUnusedField,
    updateLayoutField,
    fieldInsertLayout,
    updateFieldInLayout,
    deletedFieldsInArea,
    onClearLayout
  };
};

export type layoutStore = ReturnType<typeof useLayout>;